import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter } from "react-router-dom"
import { Toaster } from "react-hot-toast"
import App from "@/App"
import {
  isStaleChunkError,
  recentlyAutoReloaded,
  scheduleAutoReload,
} from "@/lib/app-update"
import { isInstalledPwa } from "@/lib/pwa"
import { PwaDisabledScreen } from "@/components/PwaDisabledScreen"
import "@/styles/globals.css"

// ── Self-heal: auto-recovery de chunks defasados pós-deploy ──────────────
//
// Três camadas progressivas:
//
// 1) vite:preloadError  — Vite emite ANTES do erro virar exceção React.
// 2) window error/unhandledrejection — captura dynamic imports que não
//    passaram pelo preload do Vite (ex.: import() direto em código).
// 3) ErrorBoundary (ErrorBoundary.tsx) — rede de segurança final no React.
//
// Todas usam o mesmo loop-guard de app-update.ts (sessionStorage com
// cooldown de 30s) para NUNCA entrar em reload infinito.

window.addEventListener("vite:preloadError", (event) => {
  if (recentlyAutoReloaded()) return
  console.warn("[creditos] vite:preloadError detected — auto-reloading", event)
  // `payload` traz o erro original do Vite; dele sai a URL do chunk a reparar.
  scheduleAutoReload(1000, (event as { payload?: unknown }).payload ?? event)
})

// Camada 2: erros de chunk que escapam do vite:preloadError
window.addEventListener("error", (event) => {
  const error = event.error ?? event.message
  if (isStaleChunkError(error)) {
    if (recentlyAutoReloaded()) return
    console.warn("[creditos] stale chunk error (global) — auto-reloading", event)
    event.preventDefault()
    scheduleAutoReload(1000, error)
  }
})

window.addEventListener("unhandledrejection", (event) => {
  if (isStaleChunkError(event.reason)) {
    if (recentlyAutoReloaded()) return
    console.warn(
      "[creditos] stale chunk rejection (global) — auto-reloading",
      event,
    )
    event.preventDefault()
    scheduleAutoReload(1000, event.reason)
  }
})

const rootElement = document.getElementById("root")
if (!rootElement) throw new Error("Root element not found")

// PWA desativado: se o CreditOS foi aberto como APP INSTALADO (standalone),
// bloqueia e manda usar o navegador. Aba normal do navegador segue normal.
createRoot(rootElement).render(
  <StrictMode>
    {isInstalledPwa() ? (
      <PwaDisabledScreen />
    ) : (
      <BrowserRouter>
        <App />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "var(--glass-bg)",
              backdropFilter: "blur(20px)",
              border: "1px solid var(--glass-border)",
              color: "var(--text-primary)",
              borderRadius: "12px",
              fontSize: "14px",
            },
          }}
        />
      </BrowserRouter>
    )}
  </StrictMode>,
)
